class Password {
  /**
   * @type {string} The character keyspace that's being used for generating a string of characters.
   */
  characterKeyspace = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

  /**
   * @type {string} The characters that are ambiguous. These characters will be skipped when
   *                generating a non-ambiguous password.
   */
  ambiguousCharacters = 'iIoOlL';

  /**
   * @type {string} The number keyspace that's being used for generating a string of numbers.
   */
  numberKeyspace = '0123456789';

  /**
   * @type {string} The numbers that are ambiguous. These numbers will be skipped when generating a
   *                non-ambiguous password.
   */
  ambiguousNumbers = '01';

  /**
   * @type {string|null} The generated password.
   */
  password = null;

  /**
   * @type {number} The total length of the generated password.
   */
  passwordLength = 16;

  /**
   * @type {number} The amount of numbers that are being generated into the password.
   */
  amountOfNumbers = 2;

  /**
   * @type {boolean} If the generated password will be all uppercase.
   */
  isUppercase = false;

  /**
   * @type {boolean} If the generated password will be all lowercase.
   */
  isLowercase = false;

  /**
   * @type {boolean} If the generated password will be non-ambiguous.
   */
  isNonAmbiguous = false;

  /**
   * Create and return a new password. A password will have two numbers and is twelve places long by
   * default.
   *
   * @returns {string} The generated password.
   */
  create() {
    const length = this.passwordLength - this.amountOfNumbers;
    const password = this.randomCharacters(length) + this.randomNumbers(this.amountOfNumbers);

    // Create a new array of the password string and sort the password in random order.
    const passwordArray = password.split('').sort(() => 0.5 - Math.random());

    this.password = passwordArray.join('');

    if (this.isUppercase) {
      this.password = this.password.toUpperCase();
    } else if (this.isLowercase) {
      this.password = this.password.toLowerCase();
    }

    return this.password;
  }

  /**
   * Set the length of the password that will be generated.
   *
   * @param {int} length The length of the password that will be generated.
   *
   * @returns {Object} The instance of this object.
   */
  setLength(length) {
    this.passwordLength = length;

    return this;
  }

  /**
   * Set the amount of numbers that must be present in the generated password.
   *
   * @param {int} amountOfNumbers The amount of numbers.
   *
   * @returns {Object} The instance of this object.
   */
  setAmountOfNumbers(amountOfNumbers) {
    this.amountOfNumbers = amountOfNumbers;

    return this;
  }

  /**
   * Make sure the generated password is all uppercase.
   *
   * @returns {Object} The instance of this object.
   */
  uppercase() {
    this.isUppercase = true;
    this.isLowercase = false;

    return this;
  }

  /**
   * Make sure the generated password is all lowercase.
   *
   * @returns {Object} The instance of this object.
   */
  lowercase() {
    this.isLowercase = true;
    this.isUppercase = false;

    return this;
  }

  /**
   * Make sure the generated password is non-ambiguous.
   *
   * @returns {Object} The instance of this object.
   */
  nonAmbiguous() {
    this.isNonAmbiguous = true;

    return this;
  }

  /**
   * Get the current password.
   * It won't generate a new one, but returns the already generated password.
   *
   * @returns {string} The generated password.
   */
  get() {
    return this.password;
  }

  /**
   * Get the correct character keyspace.
   *
   * @returns {string} The correct character keyspace that will be used for generating the token.
   */
  getCharacterKeyspace() {
    if (this.isNonAmbiguous) {
      const ambiguousCharacterArray = this.ambiguousCharacters.split('');

      return this.characterKeyspace
        .split('')
        .filter((character) => !ambiguousCharacterArray.includes(character))
        .join('');
    }

    return this.characterKeyspace;
  }

  /**
   * Get the correct number keyspace.
   *
   * @returns {string} The correct number keyspace that will be used for generating the token.
   */
  getNumberKeyspace() {
    if (this.isNonAmbiguous) {
      const ambiguousNumberArray = this.ambiguousNumbers.split('');

      return this.numberKeyspace
        .split('')
        .filter((character) => !ambiguousNumberArray.includes(character))
        .join('');
    }

    return this.numberKeyspace;
  }

  /**
   * Generate a random string made of characters.
   *
   * @param {int} length The amount of characters.
   *
   * @returns {string} The generated characters.
   */
  randomCharacters(length) {
    return this.randomString(length, this.getCharacterKeyspace());
  }

  /**
   * Generate a random string made of numbers.
   *
   * @param {int} length The amount of numbers.
   *
   * @returns {string} The generated numbers.
   */
  randomNumbers(length) {
    return this.randomString(length, this.getNumberKeyspace());
  }

  /**
   * Generate a random string.
   *
   * @param {number} length The length of the string.
   * @param {string} keyspace The keyspace used for generating the string.
   *
   * @returns {string} The generated string.
   */
  randomString = (length, keyspace) => {
    const randomCharacter = () => keyspace.charAt(Math.floor(Math.random() * keyspace.length));

    return [...Array(length).fill('').map(randomCharacter)].join('');
  };
}

export default () => new Password();
